import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteOfferSettingMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = [] as const;

export function BusinessCaseOfferValidityOverview() {
  const navigate = useNavigate();
  const [deleteOfferSetting] = useDeleteOfferSettingMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', () => {
        navigate(composePath(settingsRoutes.businessCaseOfferValidityDetail, {params: {id}}));
      })
      .with('delete', () => {
        openDeleteDialog({
          onConfirm: () =>
            deleteOfferSetting({settingId: id})
              .unwrap()
              .then(() => refreshData())
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .catch(handleApiError),
          text: i18n.t('page.offerValidity.labels.deleteConfirm'),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const title = i18n.t('page.settings.labels.offerValidity');

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs: [
            {
              label: i18n.t('page.businessCases.title'),
              href: settingsRoutes.businessCase,
            },
          ],
          actions: [
            {
              type: 'button',
              onClick: () => navigate(settingsRoutes.businessCaseOfferValidityNew),
              title: i18n.t('page.offerValidity.labels.new'),
            },
          ],
        }}
        data-testid={testIds.settings.businessCaseOfferValidity('settingsTemplate')}
      >
        <DmsDataGrid
          gridCode="offer-setting"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.businessCaseOfferValidity('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
